import { all, call, cps, fork, put, takeEvery, takeLeading } from 'redux-saga/effects'
import {
  AUTHORIZE_USER,
  AUTHENTICATE_USER_OTP,
  CHECK_PROFILE_STATUS,
  CREATE_CUSTOMER_FOR_USER,
  SIGNIN_USER_CALLBACK,
  SIGNOUT_USER,
  SIGNUP_USER,
  GET_ACTIVE_USER
} from 'constants/ActionTypes'
import {
  showMessage,
  authFailure,
  userProfileUpdate,
  userSignInInitiated,
  userSignInSuccess,
  userSignOutSuccess,
  userSignInCompleted,
  get_active_user
} from 'actions'
import auth0 from 'auth0-js'
import { defaultTo } from 'ramda'
import { history } from '../store'
import { getConfiguredClient } from 'util/graphqlClient'
import gql from 'graphql-tag'
import { create as customerCreateMutation } from '../Linkgua/components/CustomerDetails'
const domain = process.env.AUTH0_DOMAIN
const clientId = process.env.AUTH0_CLIENT_ID
const audience = process.env.AUTH0_AUDIENCE
const connection = process.env.AUTH0_CONNECTION
const thisHost = process.env.THIS_HOST
const webAuth = new auth0.WebAuth({
  domain: domain,
  clientID: clientId,
  redirectUri: `${thisHost}/signin`,
  audience: audience,
  responseType: 'token id_token',
  scope: 'openid email'
})

import debug from 'debug'

const sagaActionLog = debug('linkgua:saga:action')
const authLog = debug('linkgua:auth:saga')

export const profileStatusQuery = gql`
  query getProfile {
    login {
      id
      type
      profileStatus
      firstName
      lastName
      settings {
        locale
      }
      impersonatedSession
    }
  }`

function * signOut ({payload}) {
  try {
    authLog('Signing out')
    yield call([webAuth, webAuth.logout], {
      clientID: clientId,
      returnTo: thisHost + defaultTo('/', payload.returnTo)
    })
    authLog('Signed out, updating user')
    yield put(userSignOutSuccess())
    authLog('User updated')
  } catch (error) {
    yield put(authFailure(error))
  }
}

function * handleCallback ({payload}) {
  try {
    authLog('receiving callback from auth0')
    authLog(payload, '<<<===Step 1===')
    yield put(userSignInInitiated(payload.hash))
    authLog(payload, '<<<===== Step 2 ===')
    const authUser = yield cps([webAuth, webAuth.parseHash], {hash: payload.hash})
    authLog('callback parsed, updating User')
    yield put(userSignInSuccess(authUser))
    authLog('User updated, getting status from api')
    yield call(updateProfileStatus)
    authLog('Status fetched from api')
    yield put(userSignInCompleted())
    authLog('Signin completed')
    if (payload.returnTo) {
      authLog(`callback had 'returnTo', redirecting to ${payload.returnTo}`)
      yield call(history.push, payload.returnTo)
    }
  } catch (error) {
    authLog('handleCallback: error', error)
    yield put(authFailure(error))
  }
}

function * updateProfileStatus () {
  const graphqlClient = getConfiguredClient()
  const query = profileStatusQuery
  const profileQuery = yield call(graphqlClient.query, {query})
  yield put(userProfileUpdate(profileQuery.data.login))
}

function * callAuth0 ({payload}) {
  try {
    authLog('calling auth0 for login')
    if (payload.email) {
      yield put(get_active_user(payload.email));
      yield cps([webAuth, webAuth.passwordlessStart], {
        connection: connection,
        send: 'code', // TODO update to code, if left null defaults to link
        email: payload.email
      });
      yield call(history.push, payload.returnTo)
    } else {
      const silentAuthResult = yield cps([webAuth, webAuth.checkSession], { connection })
      authLog('silent auth - result', silentAuthResult)
      yield put(userSignInSuccess(silentAuthResult))
    }
  } catch (error) {
    yield put(authFailure(error))
  }
}

function * validateAuth0 ({payload}) {
  try{
    authLog('validating OTP for auth0 login');
    if (payload.email && payload.otp) {
      yield cps([webAuth, webAuth.passwordlessLogin], {
        connection: connection,
        verificationCode: payload.otp, // TODO pass code sent in email
        email: payload.email
      });
      yield call(history.push, payload.returnTo)
    } else {
      const silentAuthResult = yield cps([webAuth, webAuth.checkSession], { connection });
      authLog('silent auth - result', silentAuthResult);
      yield put(userSignInSuccess(silentAuthResult));
    } 
  } catch (error) {
    yield put(authFailure(error));
  }
}

function * signUpAtAuth0 ({payload}) {
  try {
    authLog('calling auth0 for signup')
    yield put(get_active_user(payload.email));
    yield cps([webAuth, webAuth.passwordlessStart], {
      connection: connection,
      send: 'code', // TODO update to code, if left null defaults to link
      email: payload.email,
      redirectUri: `${thisHost}/signup/${payload.type}`,
    });
    yield call(history.push, payload.returnTo)
  } catch (error) {
    authLog('signUpAtAuth0: error', error)
    yield put(authFailure(error))
  }
}

function * callApiWithCreateUser ({payload}) {
  try {
    const graphqlClient = getConfiguredClient()
    yield call(graphqlClient.mutate, {
      mutation: customerCreateMutation
    })
    yield call(updateProfileStatus)
  } catch (error) {
    yield put(showMessage(error))
  }
}

export function * authorizeUser () {
  yield takeEvery(AUTHORIZE_USER, callAuth0)
}

export function * authenticateUserOtp () {
  yield takeEvery(AUTHENTICATE_USER_OTP, validateAuth0)
}

function * signUpUser () {
  yield takeEvery(SIGNUP_USER, signUpAtAuth0)
}

export function * signInUserCallback () {
  yield takeEvery(SIGNIN_USER_CALLBACK, handleCallback)
}

export function * signOutUser () {
  yield takeEvery(SIGNOUT_USER, signOut)
}

export function * createCustomer () {
  yield takeLeading(CREATE_CUSTOMER_FOR_USER, callApiWithCreateUser)
}

export function * checkProfileStatus () {
  yield takeEvery(CHECK_PROFILE_STATUS, updateProfileStatus)
}

export function * watchAnyAction () {
  yield takeEvery('*', function * logger (action) {
    sagaActionLog('=== SAGA ACTION ===')
    sagaActionLog(action)
    sagaActionLog('===================')
  })
}

export default function * rootSaga () {
  yield all([
    fork(signUpUser),
    fork(authorizeUser),
    fork(authenticateUserOtp),
    fork(signOutUser),
    fork(signInUserCallback),
    fork(watchAnyAction),
    fork(createCustomer),
    fork(checkProfileStatus)
  ])
}



// WEBPACK FOOTER //
// ./sagas/Auth.js